import type {
  AdminStorefrontDetail,
  AdminStorefrontListItem,
  StorefrontClaimFinding,
} from '@/lib/api/storefrontAdminHooks';

import { faqChanges, parseFaq } from './storeDiff';
import { isAfter, isRealMedicalGroup } from './storefrontFormat';

/*
 * What an admin has to check before a store goes (or stays) live:
 *
 *   1. Claims: owner-written, patient-facing copy must make no medical or
 *      results claims. `findClaims` mirrors the backend's owner-save screen
 *      (system-backend app/services/storefront_claims.py: same rules, same
 *      negation and question exceptions), so the admin sees what an owner
 *      save would now refuse, including copy saved before the screen existed.
 *      Keep the two rule lists in step. Three extra rules marked advisory
 *      (implied endorsement, results in a time frame, fat burning) are for
 *      the admin's judgment only.
 *   2. Store rules: a reserved slug, the SYSTEM wordmark in the name, a
 *      placeholder medical group, a live store with no owner. A live store on
 *      a reserved slug is "live but not served": the public store and checkout
 *      answer 404 for it.
 */

interface Rule {
  id: string;
  label: string;
  pattern: RegExp;
  /** Allowed after a negation ("doesn't cure", "not FDA-approved"). */
  negatable?: boolean;
  /** Allowed inside a question ("Is it FDA-approved?"). */
  questionOk?: boolean;
}

const UNITS = '(?:lbs?|pounds?|kgs?|kilos?|kilograms?|inches|inch|sizes?|%|percent)';

const RULES: Rule[] = [
  { id: 'cure', label: 'Cure claim', pattern: /\bcur(?:e|es|ed|ing)\b/gi, negatable: true },
  {
    id: 'treat',
    label: 'Treatment claim',
    pattern: /\btreat(?:s|ed|ing)\b|\btreat\b(?!\s+(?:yourself|your\s*self|you)\b)/gi,
    negatable: true,
  },
  { id: 'prevent', label: 'Prevention claim', pattern: /\bprevent(?:s|ed|ing|ion)?\b/gi, negatable: true },
  { id: 'heal', label: 'Healing claim', pattern: /\bheal(?:s|ed|ing)?\b/gi, negatable: true },
  {
    id: 'reverse_aging',
    label: 'Reverses aging',
    pattern: /\brevers(?:e|es|ed|ing)\s+(?:the\s+)?(?:signs\s+of\s+)?(?:aging|ageing|age)\b/gi,
  },
  {
    id: 'proven',
    label: 'Clinically or scientifically proven',
    pattern: /\b(?:clinically|scientifically|medically)[\s-]+(?:proven|tested|validated|shown)\b/gi,
  },
  { id: 'proven_to', label: 'Proven results', pattern: /\bproven\s+(?:to|results?|formula)\b/gi, negatable: true },
  {
    id: 'fda',
    label: 'FDA approval',
    pattern: /\bfda[\s-]*(?:approved|cleared)\b/gi,
    negatable: true,
    questionOk: true,
  },
  { id: 'guarantee', label: 'Guarantee', pattern: /\bguarantee(?:s|d)?\b/gi, negatable: true },
  {
    id: 'no_side_effects',
    label: 'No side effects',
    pattern:
      /\b(?:no|zero|without|free\s+of)\s+(?:any\s+)?side[\s-]*effects?\b|\bside[\s-]*effect[\s-]*free\b/gi,
  },
  { id: 'risk_free', label: 'Risk-free', pattern: /\brisk[\s-]*free\b/gi },
  {
    id: 'hundred_percent',
    label: '100% safe or effective',
    pattern: /\b100\s*%\s*(?:safe|effective|natural|results?)\b/gi,
  },
  { id: 'safe_effective', label: 'Safe and effective', pattern: /\bsafe\s+(?:and|&)\s+effective\b/gi },
  {
    id: 'weight_amount',
    label: 'Weight-loss amount',
    pattern: new RegExp(
      `\\b(?:lose|lost|losing|drop|dropped|dropping|shed|shedding|melt|burn)\\s+(?:up\\s+to\\s+|over\\s+|more\\s+than\\s+)?\\d+(?:\\.\\d+)?\\s*${UNITS}(?=\\W|$)`,
      'gi',
    ),
  },
  {
    id: 'weight_time',
    label: 'Weight-loss result in a time frame',
    pattern: /\b\d+(?:\.\d+)?\s*(?:lbs?|pounds?|kgs?)\s+(?:in|within)\s+\d+\s*(?:days?|weeks?|months?)\b/gi,
  },
  { id: 'miracle', label: 'Miracle', pattern: /\bmiracle\b/gi },
  { id: 'before_after', label: 'Before and after', pattern: /\bbefore\s*(?:and|&|\/|\+)\s*after\b/gi },
  {
    id: 'instant_results',
    label: 'Instant or permanent results',
    pattern: /\b(?:instant|permanent|immediate)\s+results?\b/gi,
  },
  // Admin-only advisories (the owner save does not refuse these): implied
  // medical endorsement, results promised in a time frame, fat burning.
  {
    id: 'endorsement',
    label: 'Implied medical endorsement (advisory)',
    pattern: /\b(?:doctor|physician|clinician|md)[\s-]+(?:approved|recommended|endorsed|backed)\b/gi,
  },
  {
    id: 'results_time',
    label: 'Results in a time frame (advisory)',
    pattern:
      /\b(?:results?|see\s+(?:results?|changes?|a\s+difference))\b[^.!?\n]{0,30}?\b(?:in|within)\s+(?:just\s+)?\d+\s*(?:days?|weeks?)\b/gi,
  },
  {
    // The house name in copy, also as a lookalike ("System L4bs"); the
    // owner save refuses only the exact spellings.
    id: 'house_brand',
    label: 'Our brand name (advisory)',
    pattern:
      /\bs[yi1]st[e3]m\s*[-_.]?\s*(?:[l1|]\s*[a4@]\s*b[s5z]?|laborator(?:y|ies))\b|\bsystemlabs\b/gi,
  },
  {
    id: 'fat_burning',
    label: 'Fat-burning claim (advisory)',
    pattern: /\bfat[\s-]*burn(?:ing|er|ers)?\b|\bburns?\s+fat\b/gi,
  },
];

const NEGATION =
  /\b(?:not|never|no|cannot|can't|cant|won't|doesn't|don't|isn't|aren't|wasn't|n't)\b[^.!?\n]{0,30}$/i;

function sentenceBounds(text: string, start: number, end: number): [number, number] {
  const left = Math.max(...['.', '!', '?', '\n'].map((ch) => text.lastIndexOf(ch, start - 1))) + 1;
  const rights = ['.', '!', '?', '\n'].map((ch) => text.indexOf(ch, end)).filter((i) => i !== -1);
  return [left, rights.length ? Math.min(...rights) + 1 : text.length];
}

export interface ClaimMatch {
  rule: string;
  label: string;
  start: number;
  end: number;
  phrase: string;
  /** `block`: an owner save refuses it; `review`: a person decides. */
  severity: 'block' | 'review';
}

/** Every claim phrase in `text`, in reading order (overlaps dropped). */
export function findClaims(text: string | null | undefined): ClaimMatch[] {
  if (!text || !text.trim()) return [];
  const found: ClaimMatch[] = [];
  for (const rule of RULES) {
    rule.pattern.lastIndex = 0;
    for (const match of text.matchAll(rule.pattern)) {
      const start = match.index ?? 0;
      const end = start + match[0].length;
      const [left, right] = sentenceBounds(text, start, end);
      if (rule.negatable && NEGATION.test(text.slice(left, start))) continue;
      if (rule.questionOk && text.slice(left, right).trimEnd().endsWith('?')) continue;
      found.push({
        rule: rule.id,
        label: rule.label,
        start,
        end,
        phrase: match[0],
        severity: rule.label.includes('(advisory)') ? 'review' : 'block',
      });
    }
  }
  found.sort((a, b) => a.start - b.start || b.end - a.end);
  const out: ClaimMatch[] = [];
  for (const m of found) {
    const last = out[out.length - 1];
    if (last && m.start < last.end) continue;
    out.push(m);
  }
  return out;
}

export interface CopyEntry {
  key: string;
  /** "Hero · Headline", "FAQ 2 · Answer". */
  label: string;
  text: string;
  /** Hidden section: not shown to patients today, still worth a look. */
  hidden?: boolean;
  claims: ClaimMatch[];
}

const SECTION_NAME: Record<string, string> = {
  hero: 'Hero',
  trust: 'Trust strip',
  how_it_works: 'How it works',
  products: 'Products',
  featured_product: 'Featured product',
  about: 'About',
  lifestyle: 'Lifestyle',
  faq: 'FAQ',
};

const FIELD_NAME: Record<string, string> = {
  headline: 'Headline',
  subheadline: 'Subheadline',
  cta_label: 'Button',
  title: 'Title',
  body: 'Body',
  blurb: 'Blurb',
};

function entry(key: string, label: string, text: unknown, hidden?: boolean): CopyEntry | null {
  if (typeof text !== 'string' || !text.trim()) return null;
  return { key, label, text, hidden, claims: findClaims(text) };
}

/** A backend finding's field path as a copy entry key: `headline`,
 *  `design.hero.headline` → `hero.headline`, `design.faq.items.2.a` →
 *  `faq.2.a`. */
function findingKey(field: string): string {
  const parts = field.split('.');
  if (parts[0] !== 'design') return field;
  if (parts[2] === 'items' && parts.length >= 5) return `${parts[1]}.${parts[3]}.${parts[4]}`;
  return parts.slice(1).join('.');
}

const RULE_LABEL: Record<string, string> = {
  ...Object.fromEntries(RULES.map((rule) => [rule.id, rule.label.replace(' (advisory)', '')])),
  // The backend scan's own rules (storefront_claims.py), named for what they
  // catch: `aging_skin` flags "longevity" and "live longer", not only aging.
  aging_skin: 'Aging, longevity or skin claim',
  boost_hormones: 'Hormone claim',
  higher_hormones: 'Hormone claim',
  hormones: 'Hormone claim',
  drug_comparison: 'Comparison with a prescription drug',
  prescription_bypass: 'No-prescription claim',
  disease_marker: 'Disease marker claim',
  fast_effect: 'Fast results',
  weight_management: 'Weight claim',
  appetite: 'Appetite claim',
  muscle: 'Muscle claim',
  sexual_health: 'Sexual health claim',
  wellness_claims: 'Detox or wellness claim',
  boost_claims: 'Boost or improvement claim',
  performance_claims: 'Performance claim',
  regulatory: 'FDA, study or doctor-approval claim',
  timeline: 'Results in a time frame',
  numbers: 'Numbers in a results claim',
  transformation: 'Transformation claim',
};

function ruleLabel(rule: string, category: string | null | undefined): string {
  if (RULE_LABEL[rule]) return RULE_LABEL[rule];
  const words = (category || rule).replace(/[_-]+/g, ' ').trim();
  return words ? words[0].toUpperCase() + words.slice(1) : 'Flagged phrase';
}

/** Flags for an entry from the backend's scan (offsets into the field's
 *  own text; a finding whose offsets do not fit the text is placed by its
 *  phrase instead). */
function backendClaims(
  text: string,
  findings: StorefrontClaimFinding[],
): ClaimMatch[] {
  const out: ClaimMatch[] = [];
  for (const f of findings) {
    let start = f.start;
    let end = f.end;
    if (!(start >= 0 && end > start && end <= text.length && text.slice(start, end) === f.phrase)) {
      const at = text.toLowerCase().indexOf((f.phrase ?? '').toLowerCase());
      if (at < 0) continue;
      start = at;
      end = at + f.phrase.length;
    }
    out.push({
      rule: f.rule,
      label: ruleLabel(f.rule, f.category),
      start,
      end,
      phrase: text.slice(start, end),
      severity: f.severity === 'block' ? 'block' : 'review',
    });
  }
  out.sort((a, b) => a.start - b.start || b.end - a.end);
  return out.filter((m, i) => i === 0 || m.start >= out[i - 1].end);
}

/** Every patient-facing string the owner controls: store name, headline and
 *  subheadline, then each page section's text in page order (hidden
 *  sections last), FAQ questions and answers, and promo descriptions.
 *
 *  Flags come from the backend's claims scan (`claim_findings`) when the
 *  detail carries it, so the screen and an owner save use one rule set;
 *  an older backend's detail falls back to the mirror above. Coupon
 *  descriptions, which the backend does not scan as page copy, always use
 *  the mirror. Either way a flag is an aid: no flag is not a clearance. */
export function collectStoreCopy(store: AdminStorefrontDetail): CopyEntry[] {
  const backend = Array.isArray(store.claim_findings) ? store.claim_findings : null;
  const byKey = new Map<string, StorefrontClaimFinding[]>();
  for (const f of backend ?? []) {
    const key = findingKey(f.field);
    byKey.set(key, [...(byKey.get(key) ?? []), f]);
  }
  const make = (key: string, label: string, text: unknown, hidden?: boolean): CopyEntry | null => {
    const e = entry(key, label, text, hidden);
    if (e && backend) e.claims = backendClaims(e.text, byKey.get(key) ?? []);
    return e;
  };
  const out: (CopyEntry | null)[] = [
    make('name', 'Store name', store.name),
    make('headline', 'Headline', store.headline),
    make('subheadline', 'Subheadline', store.subheadline),
    make('creator_name', 'Creator name', store.creator_name_custom ?? null),
    // The owner's own byline as the page shows it (`{name}` filled with the
    // creator name; the claims scan reads that text); the default
    // "Curated by …" line is template copy.
    make(
      'byline.text',
      'Byline',
      store.design?.byline?.mode === 'custom'
        ? (store.design.byline.text_resolved ?? store.design.byline.text ?? null)
        : null,
    ),
  ];
  const seen = new Set(out.filter(Boolean).map((e) => e!.text.trim()));
  const sections = store.design?.sections ?? [];
  const ordered = [...sections.filter((s) => s.enabled), ...sections.filter((s) => !s.enabled)];
  for (const section of ordered) {
    const name = SECTION_NAME[section.key] ?? section.key;
    const content = (section.content ?? {}) as Record<string, unknown>;
    for (const [field, value] of Object.entries(content)) {
      if (field === 'items' && Array.isArray(value)) {
        value.forEach((item, i) => {
          const qa = (item ?? {}) as Record<string, unknown>;
          out.push(make(`${section.key}.${i}.q`, `${name} ${i + 1} · Question`, qa.q, !section.enabled));
          out.push(make(`${section.key}.${i}.a`, `${name} ${i + 1} · Answer`, qa.a, !section.enabled));
        });
        continue;
      }
      if (!FIELD_NAME[field] || typeof value !== 'string') continue;
      // The hero repeats the store headline until the owner edits it.
      if (section.key === 'hero' && seen.has(value.trim())) continue;
      out.push(make(`${section.key}.${field}`, `${name} · ${FIELD_NAME[field]}`, value, !section.enabled));
    }
  }
  for (const promo of store.promo_codes) {
    out.push(entry(`promo.${promo.id ?? promo.code}`, `Coupon ${promo.code} · Description`, promo.description));
  }
  return out.filter((e): e is CopyEntry => e !== null);
}

// ---------------------------------------------------------------- photos

export interface StoreMedia {
  key: 'logo' | 'hero' | 'lifestyle' | 'about';
  label: string;
  url: string;
  /** In a hidden section: not shown to patients today. */
  hidden: boolean;
  /** Uploaded or replaced after the last review (or never reviewed). */
  newSinceReview: boolean;
}

/** Events that move the review baseline (the backend's `reviewed_at`): a
 *  store review, an approved change, a revert to the approved version and
 *  "mark reviewed". */
const REVIEW_BASELINE_ACTIONS = new Set([
  'approved',
  'changes_requested',
  'rejected',
  'changes_approved',
  'reverted',
  'marked_reviewed',
]);

/** When the store was last reviewed: the later of `reviewed_at` and the
 *  latest event that moves the baseline, so edits a change approval, a
 *  revert or "mark reviewed" covered are not listed as unreviewed. */
export function lastReviewAt(store: Pick<AdminStorefrontDetail, 'events' | 'reviewed_at'>): string | null {
  const times = [
    ...store.events.filter((e) => REVIEW_BASELINE_ACTIONS.has(e.action)).map((e) => e.created_at),
    store.reviewed_at,
  ].filter((t): t is string => Boolean(t) && Number.isFinite(Date.parse(t as string)));
  if (times.length === 0) return null;
  return times.reduce((latest, t) => (Date.parse(t) > Date.parse(latest) ? t : latest));
}

type ReviewBaselineStore = Pick<
  AdminStorefrontDetail,
  'review_status' | 'base_review_status' | 'edited_since_review' | 'updated_at' | 'reviewed_at'
>;

/** "Edited since approval": an approved store (a change may be waiting)
 *  with field changes recorded since its last review. The backend's review
 *  baseline when it sends one (published or direct edits only, never a
 *  draft or a change still waiting); an older backend: `updated_at >
 *  reviewed_at`. */
export function editedSinceApproval(store: ReviewBaselineStore): boolean {
  const approved =
    store.review_status === 'changes_pending_review' ||
    (store.base_review_status ?? store.review_status) === 'approved';
  return approved && (store.edited_since_review ?? isAfter(store.updated_at, store.reviewed_at));
}

/** Field edits recorded after `since` (oldest first): the rows the
 *  backend counts as `edits_since_review` when `since` is the last review. */
export function editsSince(
  store: Pick<AdminStorefrontDetail, 'events'>,
  since: string | null,
): AdminStorefrontDetail['events'] {
  return store.events
    .filter(
      (e) =>
        e.kind === 'change' &&
        e.field &&
        e.action === 'edited' &&
        (!since || Date.parse(e.created_at) > Date.parse(since)),
    )
    .sort((a, b) => Date.parse(a.created_at) - Date.parse(b.created_at));
}

/** What the reviewer compares the copy against: the owner's resubmission
 *  against the last changes request, or an approved store's edits against
 *  its last review. Null when neither applies. */
export function changeReference(
  store: AdminStorefrontDetail,
): { since: string; label: string } | null {
  const lastRequest = store.events.find((e) => e.action === 'changes_requested') ?? null;
  if (lastRequest && (store.review_status === 'submitted' || store.review_status === 'changes_requested')) {
    return { since: lastRequest.created_at, label: 'Changed since the request' };
  }
  const reviewed = lastReviewAt(store);
  if (reviewed && editedSinceApproval(store)) return { since: reviewed, label: 'Changed since review' };
  return null;
}

/** Copy panel keys (`collectStoreCopy`) whose text was edited after
 *  `since`: plain fields, section fields, the FAQ items that were added or
 *  reworded, and coupon descriptions. */
export function copyKeysChangedSince(store: AdminStorefrontDetail, since: string): Set<string> {
  const out = new Set<string>();
  const edits = editsSince(store, since);
  const faqFirstOld = new Map<string, unknown>();
  const faqLastNew = new Map<string, unknown>();
  for (const e of edits) {
    const field = e.field ?? '';
    const parts = field.split('.');
    if (['name', 'headline', 'subheadline', 'creator_name'].includes(field)) {
      out.add(field);
      // The hero repeats the store headline: the copy panel lists one of them.
      if (field === 'headline' || field === 'subheadline') out.add(`hero.${field}`);
    } else if (parts[0] === 'design' && parts[2] === 'items' && parts.length === 3) {
      if (!faqFirstOld.has(parts[1])) faqFirstOld.set(parts[1], e.old_value);
      faqLastNew.set(parts[1], e.new_value);
    } else if (parts[0] === 'design' && parts.length === 3) {
      out.add(`${parts[1]}.${parts[2]}`);
      if (parts[1] === 'hero' && (parts[2] === 'headline' || parts[2] === 'subheadline')) {
        out.add(parts[2]);
      }
    } else if (parts[0] === 'promo_codes' && parts[1]) {
      const promo = store.promo_codes.find((p) => p.code === parts[1]);
      if (promo) out.add(`promo.${promo.id ?? promo.code}`);
    }
  }
  for (const [section, after] of faqLastNew) {
    for (const change of faqChanges(parseFaq(faqFirstOld.get(section)), parseFaq(after))) {
      if (change.kind === 'added' || change.kind === 'question') {
        out.add(`${section}.${change.index}.q`);
        out.add(`${section}.${change.index}.a`);
      } else if (change.kind === 'answer') {
        out.add(`${section}.${change.index}.a`);
      }
    }
  }
  return out;
}

/** Every image the owner put on the page: the logo and each section's own
 *  photo (hero, lifestyle, about; the page shows it instead of the
 *  template's stock image). `newSinceReview` comes from the change events
 *  (`logo_url`, `design.{section}.photo_url`) after the last review. */
export function storeMedia(store: AdminStorefrontDetail): StoreMedia[] {
  const reviewedAt = lastReviewAt(store);
  const changedAfterReview = (field: string) =>
    !reviewedAt ||
    store.events.some(
      (e) =>
        e.kind === 'change' &&
        e.field === field &&
        Date.parse(e.created_at) > Date.parse(reviewedAt),
    );
  const out: StoreMedia[] = [];
  if (store.logo_url && store.logo_url.startsWith('https://')) {
    out.push({
      key: 'logo',
      label: 'Logo',
      url: store.logo_url,
      hidden: false,
      newSinceReview: changedAfterReview('logo_url'),
    });
  }
  const labels = { hero: 'Hero photo', lifestyle: 'Lifestyle photo', about: 'About photo' } as const;
  for (const key of ['hero', 'lifestyle', 'about'] as const) {
    const section = store.design?.sections?.find((s) => s.key === key);
    const url = (section?.content as Record<string, unknown> | undefined)?.photo_url;
    if (typeof url !== 'string' || !url.startsWith('https://')) continue;
    out.push({
      key,
      label: labels[key],
      url,
      hidden: !section?.enabled,
      newSinceReview: changedAfterReview(`design.${key}.photo_url`),
    });
  }
  return out;
}

/** The about section's photo, when the owner uploaded one. */
export function aboutPhoto(store: AdminStorefrontDetail): string | null {
  return storeMedia(store).find((m) => m.key === 'about' && !m.hidden)?.url ?? null;
}

// ---------------------------------------------------------------- coupons

export interface CouponReach {
  /** Advertised discount on the plan's charge, in cents. */
  advertisedCents: number;
  /** What the store can fund: never more than its earning (price − floor). */
  deliveredCents: number;
  productName: string;
  planLabel: string;
  priceCents: number;
}

export interface CouponCheck {
  code: string;
  description: string;
  /** Per active plan. */
  reach: CouponReach[];
  /** Plans where the advertised discount is cut down by the floor. */
  cappedPlans: number;
  /** No active plan gets any discount at all. */
  ineffective: boolean;
  /** Smallest and largest delivered share of the price (0..1). */
  minShare: number;
  maxShare: number;
}

/**
 * Coupons are store-funded: a discount comes out of the store's earning on
 * each line and never takes a price below floor, so a code can promise more
 * than it delivers ("50% off every charge" on a plan priced at floor is
 * $0 off). The backend caps the discount per line (the checkout quote's
 * `capped`); this works out the same cap per active plan so the reviewer
 * sees it before patients do.
 */
export function couponChecks(store: AdminStorefrontDetail): CouponCheck[] {
  const plans = store.products.flatMap((product) =>
    product.plans
      .filter((plan) => plan.active)
      .map((plan) => ({
        productName: product.name || product.canonical_product_id,
        planLabel: plan.interval_months === 1 || plan.plan === 'monthly' ? 'Monthly' : `Every ${plan.interval_months ?? '?'} months`,
        priceCents: plan.price_cents,
        earningCents:
          plan.earning_cents ??
          (plan.floor_cents != null ? Math.max(plan.price_cents - plan.floor_cents, 0) : null),
      })),
  );
  return store.promo_codes
    .filter((promo) => promo.active)
    .map((promo) => {
      const reach: CouponReach[] = plans
        .filter((plan) => plan.earningCents != null && plan.priceCents > 0)
        .map((plan) => {
          const advertised =
            promo.percent_off != null
              ? Math.round((plan.priceCents * promo.percent_off) / 100)
              : Math.min(promo.amount_off_cents ?? 0, plan.priceCents);
          return {
            advertisedCents: advertised,
            deliveredCents: Math.max(0, Math.min(advertised, plan.earningCents ?? 0)),
            productName: plan.productName,
            planLabel: plan.planLabel,
            priceCents: plan.priceCents,
          };
        });
      const shares = reach.map((r) => r.deliveredCents / r.priceCents);
      return {
        code: promo.code,
        description: promo.description ?? '',
        reach,
        cappedPlans: reach.filter((r) => r.deliveredCents < r.advertisedCents).length,
        ineffective: reach.length > 0 && reach.every((r) => r.deliveredCents === 0),
        minShare: shares.length ? Math.min(...shares) : 0,
        maxShare: shares.length ? Math.max(...shares) : 0,
      };
    });
}

export interface ThinPlan {
  productName: string;
  planLabel: string;
  priceCents: number;
  earningCents: number;
}

/** Active plans that earn the store nothing (priced at floor) or under 10%
 *  over floor: a coupon can take nothing off them, and the store has no
 *  room to run one. */
export function thinPlans(store: AdminStorefrontDetail): { zero: ThinPlan[]; thin: ThinPlan[] } {
  const zero: ThinPlan[] = [];
  const thin: ThinPlan[] = [];
  for (const product of store.products) {
    for (const plan of product.plans) {
      if (!plan.active || plan.floor_cents == null || plan.price_cents < plan.floor_cents) continue;
      const earning = plan.earning_cents ?? plan.price_cents - plan.floor_cents;
      const row = {
        productName: product.name || product.canonical_product_id,
        planLabel:
          plan.interval_months === 1 || plan.plan === 'monthly'
            ? 'Monthly'
            : `Every ${plan.interval_months ?? '?'} months`,
        priceCents: plan.price_cents,
        earningCents: earning,
      };
      if (earning <= 0) zero.push(row);
      else if (earning < plan.floor_cents * 0.1) thin.push(row);
    }
  }
  return { zero, thin };
}

export function claimCount(entries: CopyEntry[]): number {
  return entries.reduce((n, e) => n + e.claims.length, 0);
}

// ---------------------------------------------------------------- store rules

/** Mirrors the backend's RESERVED_SLUGS (app/services/storefronts.py). */
const RESERVED_SLUGS = new Set([
  'app', 'www', 'api', 'partners', 'partner', 'admin', 'help', 'support',
  'mail', 'email', 'store', 'stores', 'staging', 'stage', 'dev', 'test',
  'portal', 'status', 'docs', 'blog', 'cdn', 'static', 'assets', 'auth',
  'login', 'account', 'billing', 'checkout', 'intake', 'legal', 'terms',
  'privacy', 'rx', 'rxstore', 'rx-store',
  'system', 'sys', 'systm',
]);

const LOOKALIKE: Record<string, string> = {
  '0': 'o', '1': 'i', '3': 'e', '4': 'a', '5': 's', '7': 't', $: 's', '@': 'a',
};
const SYSTEM_ABBREVIATIONS = new Set(['sys', 'systm', 'syst']);
const LAB_WORDS = new Set(['lab', 'labs', 'laboratory', 'laboratories']);
const LAB_COMPOUND = /^(?:sys|systm|syst|sl)(?:lab|labs|laboratory|laboratories)$/;

/** The SYSTEM wordmark or a lookalike in a name (backend has_system_token). */
export function hasSystemToken(text: string | null | undefined): boolean {
  if (!text) return false;
  const words = text
    .toLowerCase()
    .split(/[\s\-_.]+/)
    .filter(Boolean)
    .map((token) => [
      ...new Set([
        token,
        token.replace(/[013457$@]/g, (c) => LOOKALIKE[c] ?? c),
        ...(token.match(/[a-z]+/g) ?? []),
      ]),
    ]);
  return words.some((forms, index) => {
    if (
      forms.some((form) => {
        const rest = form.replace(/ecosystems?/g, ' ');
        return rest.includes('system') || SYSTEM_ABBREVIATIONS.has(form) || LAB_COMPOUND.test(form);
      })
    ) {
      return true;
    }
    if (!forms.some((form) => LAB_WORDS.has(form))) return false;
    const near = [...(words[index - 1] ?? []), ...(words[index + 1] ?? [])];
    return near.some((form) => form === 'sl' || SYSTEM_ABBREVIATIONS.has(form));
  });
}

export function isReservedSlug(slug: string | null | undefined): boolean {
  return Boolean(slug && RESERVED_SLUGS.has(slug.trim().toLowerCase()));
}

export interface StoreProblem {
  code: 'reserved_slug' | 'system_name' | 'medical_group' | 'no_owner';
  message: string;
  /** Blocks serving or payouts (vs. a rule to fix at the next edit). */
  severe: boolean;
}

type StoreLike = Pick<AdminStorefrontListItem, 'slug' | 'name' | 'status' | 'owner_email'> &
  Partial<Pick<AdminStorefrontDetail, 'medical_group_name' | 'members' | 'medical_group_options'>>;

export function storeProblems(store: StoreLike): StoreProblem[] {
  const out: StoreProblem[] = [];
  const live = store.status === 'live';
  if (isReservedSlug(store.slug)) {
    out.push({
      code: 'reserved_slug',
      severe: live,
      message: live
        ? `“${store.slug}” is a reserved address, so the live store and its checkout answer 404. Change the slug to serve it again.`
        : `“${store.slug}” is a reserved address. Change the slug before this store can go live.`,
    });
  }
  if (hasSystemToken(store.name)) {
    out.push({
      code: 'system_name',
      severe: false,
      message: 'The store name uses the SYSTEM wordmark, which store names may not. Rename it.',
    });
  }
  if (
    live &&
    'medical_group_name' in store &&
    !isRealMedicalGroup(store.medical_group_name, store.medical_group_options)
  ) {
    out.push({
      code: 'medical_group',
      severe: true,
      message:
        store.medical_group_options && store.medical_group_options.length > 0 && store.medical_group_name
          ? `Live with “${store.medical_group_name}” as its medical group, which is not one of the groups we work with (${store.medical_group_options.join(', ')}). The store disclaimer shows it to patients. Choose the real group.`
          : 'Live with a placeholder medical group: the store disclaimer shows it to patients. Set the real group.',
    });
  }
  if (
    live &&
    store.members !== undefined &&
    !store.members.some((m) => m.access === 'owner') &&
    !store.owner_email
  ) {
    out.push({
      code: 'no_owner',
      severe: false,
      message: 'Live with no owner: nobody can edit the store, and its payouts have nobody to go to.',
    });
  }
  return out;
}

/** Live but its public store and checkout answer 404 (reserved slug). */
export function liveButNotServed(store: Pick<AdminStorefrontListItem, 'slug' | 'status'>): boolean {
  return store.status === 'live' && isReservedSlug(store.slug);
}
